import React from 'react';
import { RefreshCw, Trash2 } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Badge } from '../ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import StrategyRuntimeFields from '../StrategyRuntimeFields';
import { STRATEGY_ENGINES } from '../../lib/strategyRuntime';

const StrategiesTab = ({
  newStrategy,
  setNewStrategy,
  catalogStrategies,
  fetchCatalogStrategies,
  handleCreateCatalogStrategy,
  openEditStrategy,
  requestToggleCatalogStrategy,
  requestDeleteCatalogStrategy,
  updateCatalogRuntimeEngine,
  updateCatalogRuntimeParam,
}) => {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <Card className="bg-zinc-900 border-zinc-800">
        <CardHeader>
          <CardTitle>Upload Python Strategy</CardTitle>
          <CardDescription>Add strategy code to platform catalog</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <Input
            placeholder="Strategy name"
            value={newStrategy.name}
            onChange={(e) => setNewStrategy({ ...newStrategy, name: e.target.value })}
            className="bg-zinc-800 border-zinc-700"
          />
          <Input
            placeholder="Short description"
            value={newStrategy.description}
            onChange={(e) => setNewStrategy({ ...newStrategy, description: e.target.value })}
            className="bg-zinc-800 border-zinc-700"
          />
          <Select
            value={newStrategy.min_plan}
            onValueChange={(value) => setNewStrategy({ ...newStrategy, min_plan: value })}
          >
            <SelectTrigger className="bg-zinc-800 border-zinc-700">
              <SelectValue placeholder="Minimum plan" />
            </SelectTrigger>
            <SelectContent className="bg-zinc-900 border-zinc-800">
              <SelectItem value="FREE">FREE</SelectItem>
              <SelectItem value="BASIC">BASIC</SelectItem>
              <SelectItem value="PRO">PRO</SelectItem>
              <SelectItem value="ENTERPRISE">ENTERPRISE</SelectItem>
            </SelectContent>
          </Select>
          <Select
            value={newStrategy.runtime_engine}
            onValueChange={updateCatalogRuntimeEngine}
          >
            <SelectTrigger className="bg-zinc-800 border-zinc-700">
              <SelectValue placeholder="Execution engine" />
            </SelectTrigger>
            <SelectContent className="bg-zinc-900 border-zinc-800">
              {STRATEGY_ENGINES.map((engine) => (
                <SelectItem key={engine.value} value={engine.value}>
                  {engine.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <StrategyRuntimeFields
            engine={newStrategy.runtime_engine}
            params={newStrategy.runtime_params}
            onChange={updateCatalogRuntimeParam}
            gridClassName="grid grid-cols-1 gap-3 md:grid-cols-2"
            labelClassName="text-sm font-medium text-zinc-300"
            inputClassName="bg-zinc-800 border-zinc-700 text-sm text-white"
            switchCardClassName="flex items-center justify-between rounded-md border border-zinc-800 bg-zinc-950/60 px-3 py-3"
            hintClassName="text-xs text-zinc-500"
          />
          <textarea
            placeholder="Paste Python strategy code here..."
            value={newStrategy.code}
            onChange={(e) => setNewStrategy({ ...newStrategy, code: e.target.value })}
            className="w-full min-h-[220px] p-3 rounded-md bg-zinc-800 border border-zinc-700 text-sm font-mono"
          />
          <Button onClick={handleCreateCatalogStrategy} className="w-full">
            Upload Strategy
          </Button>
        </CardContent>
      </Card>

      <Card className="bg-zinc-900 border-zinc-800">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>Existing Catalog Strategies</CardTitle>
            <Button variant="outline" size="sm" onClick={fetchCatalogStrategies}>
              <RefreshCw className="w-4 h-4 mr-2" /> Refresh
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {catalogStrategies.length === 0 ? (
            <div className="text-zinc-500 text-sm">No catalog strategies yet.</div>
          ) : (
            <div className="space-y-3 max-h-[520px] overflow-auto pr-1">
              {catalogStrategies.map((s) => (
                <div key={s.id} className="border border-zinc-800 rounded-lg p-3">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <div className="font-semibold">{s.name}</div>
                      <div className="text-xs text-zinc-500">{s.description || 'No description'}</div>
                      <div className="mt-2 flex gap-2">
                        <Badge variant="outline">{s.min_plan}</Badge>
                        <Badge variant="outline">
                          {STRATEGY_ENGINES.find((engine) => engine.value === s.runtime_definition?.engine)?.label || 'Trend Following'}
                        </Badge>
                        <Badge variant={s.is_active ? 'default' : 'secondary'}>
                          {s.is_active ? 'Active' : 'Inactive'}
                        </Badge>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button variant="ghost" size="sm" onClick={() => openEditStrategy(s)}>
                        Edit
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => requestToggleCatalogStrategy(s)}>
                        {s.is_active ? 'Disable' : 'Enable'}
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-red-400 hover:text-red-300"
                        onClick={() => requestDeleteCatalogStrategy(s.id)}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default StrategiesTab;
